import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia';
import { DefaultHotkeys } from '@/constants/DefaultHotkeys.ts';
import type { VariableConfig } from '@/types/edit/VariableConfig.ts';
import { DefaultVariableConfigs } from '@/constants/DefaultVariableConfigs.ts';
import type { LoggingLevel } from '@/types/LoggingLevels.ts';
import type { ProfileConfig } from '@/types/edit/ProfileConfig.ts';
import { DefaultProfileConfigs } from '@/constants/DefaultProfileConfigs.ts';
import { watch } from 'vue';
import { syncToWebViewAction } from '@/actions/sync/SyncToWebViewAction.ts';
import { SYNC_TO_WEB_VIEW_KEYS } from '@/types/sync/SyncKeys.ts';

interface State {
    // Interface
    appOpacity: number;
    appAlwaysOnTop: boolean;
    darkModeEnabled: boolean;
    rememberedUsername: string;
    isOnboarding: boolean;
    hideWarningShown: boolean;
    lastSeenChangelogVersion: string | undefined;
    // Run
    selectedProfileId: ProfileConfig['id'] | undefined;
    favoriteProfileIds: ProfileConfig['id'][];
    // Variables
    globalVariables: Record<VariableConfig['key'], VariableConfig['value']>;
    dynamicVariableInputId: VariableConfig['id'] | undefined;
    dynamicVariableOutputId: VariableConfig['id'] | undefined;
    // Other
    hotkeys: Record<string, string>;
    loggingLevel: LoggingLevel;
    // Announcements
    zoomMacOsAnnouncementHidden: boolean;
    gpt5AnnouncementHidden: boolean;
}

const useSettingsStore = defineStore('v3/settings', {
    state: (): State => {
        return {
            // Interface
            appOpacity: 100,
            appAlwaysOnTop: true,
            darkModeEnabled: true,
            rememberedUsername: '',
            isOnboarding: false,
            hideWarningShown: false,
            lastSeenChangelogVersion: undefined,
            // Run
            selectedProfileId: Object.values(DefaultProfileConfigs)[0]['id'],
            favoriteProfileIds: ['6f18d96e-8bd5-46c7-928f-923b1f32ca35'],
            // Variables
            globalVariables: {},
            dynamicVariableInputId:
                DefaultVariableConfigs['7033b069-9c06-41c9-8177-2a7b3c6c669d']['id'], // PROGRAMMING_LANGUAGE_OPTIONS
            dynamicVariableOutputId:
                DefaultVariableConfigs['98d8feff-4acf-4f05-92e7-e07b04250dc1']['id'], // PROGRAMMING_LANGUAGE
            // Other
            hotkeys: JSON.parse(JSON.stringify(DefaultHotkeys)),
            loggingLevel: 'info',
            // Announcements
            zoomMacOsAnnouncementHidden: false,
            gpt5AnnouncementHidden: false,
        };
    },
    persist: {
        storage: localStorage,
    },
});

/**
 * @see storeSyncSettingsHandler
 * @see syncStoresToWebViewAction
 */
export const settingsStoreWatcherHandler = (
    lastSeenChangelogVersion: string | undefined,
    selectedProfileId: ProfileConfig['id'] | undefined,
    hotkeys: Record<string, string>,
    loggingLevel: LoggingLevel,
) => {
    syncToWebViewAction(
        SYNC_TO_WEB_VIEW_KEYS.storeSyncSettings,
        JSON.stringify({
            lastSeenChangelogVersion: lastSeenChangelogVersion,
            selectedProfileId: selectedProfileId,
            hotkeys: hotkeys,
            loggingLevel: loggingLevel,
        }),
    );
};

export const registerSettingsStoreWatcher = () => {
    const store = useSettingsStore();
    const { lastSeenChangelogVersion, selectedProfileId, hotkeys, loggingLevel } =
        storeToRefs(store);

    watch(
        [lastSeenChangelogVersion, selectedProfileId, hotkeys, loggingLevel],
        async ([
            newLastSeenChangelogVersion,
            newSelectedProfileId,
            newHotkeys,
            newLoggingLevel,
        ]) => {
            settingsStoreWatcherHandler(
                newLastSeenChangelogVersion,
                newSelectedProfileId,
                newHotkeys,
                newLoggingLevel,
            );
        },
        { deep: true },
    );
};

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useSettingsStore, import.meta.hot));
}

export { useSettingsStore };
